import 'react-circular-progressbar/dist/styles.css';

import { ChevronLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetLead } from '@/hooks/leads/useGetLead';
import { useUpdateLead } from '@/hooks/leads/useUpdateLead';
import LeadBackgroundTab from '@/pages/dashboard/lead-profile/LeadBackgroundTab';
import LeadDocumentsTab from '@/pages/dashboard/lead-profile/LeadDocumentsTab';
import LeadFinancialTab from '@/pages/dashboard/lead-profile/LeadFinancialTab';
import LeadNotesTab from '@/pages/dashboard/lead-profile/LeadNotesTab';
import LeadPersonaTab from '@/pages/dashboard/lead-profile/LeadPersonaTab';
import LeadProfileTab from '@/pages/dashboard/lead-profile/LeadProfileTab';
import { InvitationStatus } from '@/types/lead.type';

const getInviteStatusText = (
  status: InvitationStatus | null | undefined
): string => {
  switch (status) {
    case 'SENT':
      return 'Invitation Sent';
    case 'REFERRED':
      return 'Referred';
    case 'ACCEPTED':
      return 'Accepted';
    case 'REVOKED':
      return 'Revoked';
    case 'PENDING':
      return 'Pending';
    case 'NOT_SENT':
    default:
      return 'Not Sent';
  }
};

const LeadProfile = () => {
  const navigate = useNavigate();
  const { id: candidateId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNotes = searchParams.get('addNotes') === 'true';

  const [selectedTab, setSelectedTab] = useState<string>(
    isNotes ? 'notes' : 'profile'
  );

  const {
    result: leadResult,
    isLoading,
    isError,
  } = useGetLead(candidateId || '');
  const { mutate: updateLead, isLoading: isUpdating } = useUpdateLead();

  const candidate = leadResult?.data;
  const badgeLabel = getInviteStatusText(candidate?.invitation_status);

  const goBack = () => {
    navigate('/dashboard/data-center?tab=leads');
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center p-8"
        data-testid="lead-profile-loading"
      >
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (isError || !candidate) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p className="text-typo-gray-tertiary">Error loading lead data</p>
        <Button onClick={goBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lead-profile-page">
      <div className="flex w-full items-center justify-between gap-5">
        <Button
          size="icon"
          variant="ghost"
          className="flex-shrink-0"
          onClick={goBack}
        >
          <ChevronLeft className="cursor-pointer" />
        </Button>
        <div className="flex w-full items-center justify-start gap-5">
          {candidate.photo_url ? (
            <img
              src={candidate.photo_url}
              alt={candidate.full_name || 'Candidate avatar'}
              className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg"
            />
          ) : (
            <EmptyAvatar className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg" />
          )}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-2xl font-semibold text-[#101828]">
                {candidate.full_name}
              </p>
              <Badge variant="default" className="text-xs">
                {badgeLabel}
              </Badge>
            </div>
            <p className="as-body-02 as-strong text-muted-foreground">
              {candidate.email}
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={selectedTab}
        onValueChange={setSelectedTab}
        className="w-full"
      >
        <div className="flex justify-start">
          <TabsList className="mb-4">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="persona">Persona</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
            <TabsTrigger value="background">Background</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="profile"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <LeadProfileTab
            lead={candidate}
            updateLead={updateLead}
            isUpdating={isUpdating}
          />
        </TabsContent>

        <TabsContent
          value="persona"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <LeadPersonaTab candidateId={candidateId} />
        </TabsContent>

        <TabsContent
          value="financials"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <LeadFinancialTab />
        </TabsContent>

        <TabsContent
          value="background"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <LeadBackgroundTab />
        </TabsContent>

        <TabsContent
          value="documents"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          {candidateId ? <LeadDocumentsTab candidateId={candidateId} /> : null}
        </TabsContent>

        <TabsContent
          value="notes"
          className="mt-0 rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <LeadNotesTab leadId={candidateId || ''} isNotes={isNotes} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadProfile;
